import { FC, useEffect, useState } from 'react'
import { FormikFieldProps } from '../../../../components/formik-component/FieldProps'
import { InputNumber } from '../../../../components/formik-component'
import { TimeZoneAndLocale } from '../../../../types.custom'
import { useTranslation } from 'react-i18next'
import { useAmountAndCurrency } from '../../../../shared/useAmountAndCurrency'
import { useAuth } from '../../../../context/auth-context'
import { EmployeeLevelSelect } from './EmployeeLevelSelect'
import { EmployeeNationalLevelSelect } from './EmployeeNationalLevelSelect'
import { ContractChangeRow } from './ContractChangeRow'
import moment from 'moment'
import {
  CareerPathEvaluation,
  ContractChangeValidationListFragment,
  ContractTypeEnum,
  CountrySettingsFragment,
  LatestContractDtoFragment,
  EmployeeLevelEnum,
  GovernmentJobTitle,
  MeetingType,
} from '../../../../types'
import { ContractChangePayload, ContractChangeWithObjectPayload } from '../types'
import { ContractChangeCareerPath } from '../careerPath/ContractChangeCareerPath'
import { useCurrency } from '../../../../context/currency-context'
import {
  getCurrentSalaryBonusLevelNCLAndGJTValueForSelectedMonth,
  getGovernmentJobTitle,
  isMonthlySalaryInRange,
  MULTI_VALUE_SEPARATOR,
} from '../helpers'
import { createWarningMessagesForContractChanges } from './contractChangeModalutils'
import { round } from '../../../../shared/numberUtil'
import { useFormikContext } from 'formik'
import { GovernmentJobTitleSelect } from './GovernmentJobTitleSelect'
import PerText from '../../../../components/custom_components/Text'
import { BrazilianFormikInputNumber } from '../../../../components/formik-component/BrazilianInputNumber'

export type FormikContractChangePayloadProps = FormikFieldProps & {
  timezoneAndLocale: TimeZoneAndLocale
  currentContract: LatestContractDtoFragment
  validFrom: { year?: number; month?: number }
  formValues: ContractChangePayload
  careerPathEvaluation?: CareerPathEvaluation | null
  scheduledChanges: string
  isEdit: boolean
  isEmployeeLevelValidationEnabled: boolean
  countrySettings: CountrySettingsFragment
  isPreparation?: boolean
  contractChangeValidationList: ContractChangeValidationListFragment
  governmentJobTitles?: GovernmentJobTitle[]
  toggleInvalidGovernmentJobTitle?: (newValue: boolean) => void
  employeeLevels: EmployeeLevelEnum[]
  meetingType: MeetingType
  appraisalSeasonYear: number
  disabled: boolean
}

export enum ContractChangeType {
  NationalReferenceLevel = 'nationalReferenceLevel',
  GovernmentJobTitle = 'governmentJobTitle',
  EmployeeLevel = 'employeeLevel',
  Salary = 'salary',
  Bonus = 'bonus',
}

export const FormikContractChangePayload: FC<FormikContractChangePayloadProps> = ({
  name,
  timezoneAndLocale,
  currentContract,
  validFrom,
  careerPathEvaluation,
  formValues,
  scheduledChanges,
  isEdit,
  isEmployeeLevelValidationEnabled,
  countrySettings,
  isPreparation = false,
  contractChangeValidationList,
  governmentJobTitles = undefined,
  toggleInvalidGovernmentJobTitle = undefined,
  employeeLevels,
  meetingType,
  appraisalSeasonYear,
  disabled,
}) => {
  let futureChangeWarningsByCategory = new Map<ContractChangeType, string[]>()
  const [t] = useTranslation()
  const { user } = useAuth()
  const { currency: fallbackCurrency } = useCurrency()
  const { formatCurrency, calculateAmountInNewCurrency } = useAmountAndCurrency(user.locale)
  const { currencyConversionData } = useCurrency()
  const [cachedFormValues] = useState({
    nationalReferenceLevel: formValues.nationalReferenceLevel,
    governmentJobTitle: formValues.governmentJobTitle,
    employeeLevel: formValues.employeeLevel,
    salary: formValues.salary,
    bonus: formValues.bonus,
  })
  const [selectedGovernmentJobTitle, setSelectedGovernmentJobTitle] = useState<GovernmentJobTitle | undefined>(undefined)
  const [monthlyCurrentSalaryWarning, setMonthlyCurrentSalaryWarning] = useState(undefined)
  const [monthlySelectedSalaryWarning, setMonthlySelectedSalaryWarning] = useState(undefined)

  let monthlySalaryMaximumInSalaryCurrency = currentContract.contractGovernmentJobTitle
    ? currentContract.contractGovernmentJobTitle.governmentJobTitle.monthlySalaryMaximum
    : undefined
  let monthlySalaryMinimumInSalaryCurrency = currentContract.contractGovernmentJobTitle
    ? currentContract.contractGovernmentJobTitle.governmentJobTitle.monthlySalaryMinimum
    : undefined
  const currentGovernmentJobTitle = selectedGovernmentJobTitle
    ? selectedGovernmentJobTitle
    : countrySettings.hasGovernmentJobTitleReferenceLevel && currentContract.contractGovernmentJobTitle
    ? currentContract.contractGovernmentJobTitle.governmentJobTitle
    : undefined

  if (
    !!currentContract.contractGovernmentJobTitle &&
    !!currentGovernmentJobTitle &&
    !!currentContract.annualSalary &&
    currentGovernmentJobTitle.currency !== currentContract.annualSalary.currency
  ) {
    monthlySalaryMinimumInSalaryCurrency = calculateAmountInNewCurrency(
      currentGovernmentJobTitle.currency,
      currentContract.annualSalary.currency,
      currencyConversionData,
      monthlySalaryMinimumInSalaryCurrency,
    )
    monthlySalaryMaximumInSalaryCurrency = calculateAmountInNewCurrency(
      currentGovernmentJobTitle.currency,
      currentContract.annualSalary.currency,
      currencyConversionData,
      monthlySalaryMaximumInSalaryCurrency,
    )
  }

  useEffect(() => {
    if (
      !!governmentJobTitles &&
      !!governmentJobTitles.length &&
      currentContract.annualSalary !== undefined &&
      !!currentContract.contractGovernmentJobTitle &&
      !!countrySettings.hasGovernmentJobTitleReferenceLevel
    ) {
      const salaryValue =
        formValues.salary !== undefined && formValues.salary !== null && formValues.salary.toString() !== ''
          ? formValues.salary
          : currentContract.annualSalary!.amount
      const governmentJobTitleValue = !formValues.governmentJobTitle
        ? currentContract.contractGovernmentJobTitle.governmentJobTitle
        : getGovernmentJobTitle(governmentJobTitles, formValues.governmentJobTitle)
      const monthlySalaryMaximumInSalaryCurrency =
        !!governmentJobTitleValue &&
        currentContract.annualSalary &&
        currentContract.annualSalary.currency !== governmentJobTitleValue.currency
          ? calculateAmountInNewCurrency(
              governmentJobTitleValue.currency,
              currentContract.annualSalary.currency,
              currencyConversionData,
              governmentJobTitleValue.monthlySalaryMaximum,
            )
          : governmentJobTitleValue
          ? governmentJobTitleValue.monthlySalaryMaximum
          : undefined
      const monthlySalaryMinimumInSalaryCurrency =
        !!governmentJobTitleValue &&
        currentContract.annualSalary &&
        currentContract.annualSalary.currency !== governmentJobTitleValue.currency
          ? calculateAmountInNewCurrency(
              governmentJobTitleValue.currency,
              currentContract.annualSalary.currency,
              currencyConversionData,
              governmentJobTitleValue.monthlySalaryMinimum,
            )
          : governmentJobTitleValue
          ? governmentJobTitleValue.monthlySalaryMinimum
          : undefined

      setSelectedGovernmentJobTitle(governmentJobTitleValue)
      if (
        !!governmentJobTitleValue &&
        monthlySalaryMaximumInSalaryCurrency &&
        monthlySalaryMinimumInSalaryCurrency &&
        !isMonthlySalaryInRange(
          salaryValue / currentContract.monthlySalaryMonthsInYear,
          monthlySalaryMinimumInSalaryCurrency,
          monthlySalaryMaximumInSalaryCurrency,
        )
      ) {
        if (formValues.salary !== undefined && formValues.salary !== null && formValues.salary.toString() !== '') {
          setMonthlySelectedSalaryWarning(
            t('appraisalMeeting.stepContractChanges.modal.selectedMonthlySalaryOutOfRange', {
              hiringOfficeCode: governmentJobTitleValue.hiringOffice.code,
              department: governmentJobTitleValue.department,
              title: governmentJobTitleValue.title,
              separator: MULTI_VALUE_SEPARATOR,
            }),
          )
          setMonthlyCurrentSalaryWarning(undefined)
        } else {
          setMonthlyCurrentSalaryWarning(
            t('appraisalMeeting.stepContractChanges.modal.currentMonthlySalaryOutOfRange', {
              hiringOfficeCode: governmentJobTitleValue.hiringOffice.code,
              department: governmentJobTitleValue.department,
              title: governmentJobTitleValue.title,
              separator: MULTI_VALUE_SEPARATOR,
            }),
          )
          setMonthlySelectedSalaryWarning(undefined)
        }
      } else {
        setMonthlyCurrentSalaryWarning(undefined)
        setMonthlySelectedSalaryWarning(undefined)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (contractChangeValidationList.validationEntries.length) {
    const futureChanges = contractChangeValidationList.validationEntries.filter(
      (x) => new Date(x.date).getTime() > new Date().getTime() && new Date(x.date).getFullYear() === new Date().getFullYear(),
    )
    futureChangeWarningsByCategory = createWarningMessagesForContractChanges(futureChanges, t)
  }
  const hasNationalContract = currentContract.hasNationalContract
  const hasContractGovernmentTitle = !!currentContract.contractGovernmentJobTitle
  const doSelectableGovernmentJobTitlesExist = hasContractGovernmentTitle && !!governmentJobTitles

  const isNotRestricted =
    validFrom.year && validFrom.month && currentContract.updatesRestrictedTo
      ? moment(new Date(validFrom.year, validFrom.month, 15)).startOf('month').isAfter(moment(currentContract.updatesRestrictedTo))
      : true
  const isTargetBonusChangeNotAllowed =
    validFrom.year && validFrom.year < appraisalSeasonYear && meetingType === MeetingType.AppraisalUpdate

  const scheduledChangesIncludes = (type: ContractChangeType) => scheduledChanges.includes(type)

  const isNotApprenticeshipContract = currentContract.contractType.typeOfContract !== ContractTypeEnum.Apprenticeship

  const { salary, bonus, employeeLevel, nationalContractLevel, governmentJobTitle } =
    getCurrentSalaryBonusLevelNCLAndGJTValueForSelectedMonth(validFrom, contractChangeValidationList)

  const { values, setValues } = useFormikContext<ContractChangeWithObjectPayload>()

  const isRowTypeVisible = (type: ContractChangeType) => {
    switch (type) {
      case ContractChangeType.NationalReferenceLevel:
        return isEdit && cachedFormValues.nationalReferenceLevel ? true : !scheduledChangesIncludes(type)
      case ContractChangeType.GovernmentJobTitle:
        return isEdit && cachedFormValues.governmentJobTitle ? true : !scheduledChangesIncludes(type)
      case ContractChangeType.EmployeeLevel:
        return isEdit && cachedFormValues.employeeLevel ? true : !scheduledChangesIncludes(type)
      case ContractChangeType.Salary:
        return isEdit && cachedFormValues.salary !== undefined ? true : !scheduledChangesIncludes(type)
      case ContractChangeType.Bonus:
        return isEdit && cachedFormValues.bonus !== undefined ? true : !scheduledChangesIncludes(type)
      default:
        return false
    }
  }

  const onGovernmentJobTitleChange = (newValue: string) => {
    if (!!governmentJobTitles && !!governmentJobTitles.length) {
      const salaryValue =
        formValues.salary !== undefined && formValues.salary !== null && formValues.salary.toString() !== ''
          ? formValues.salary
          : currentContract.annualSalary!.amount
      const governmentJobTitleValue = !newValue || newValue === '' ? undefined : getGovernmentJobTitle(governmentJobTitles, newValue)
      const governmentJobTitleStringValue = !newValue || newValue === '' ? undefined : newValue
      const employeeLevel = !!governmentJobTitleValue ? governmentJobTitleValue.level : undefined
      const currentContractContractGovernmentJobTitle = currentContract.contractGovernmentJobTitle!
      const areCurrenciesTheSame = !!governmentJobTitleValue
        ? governmentJobTitleValue!.currency === currentContract.annualSalary!.currency
        : currentContractContractGovernmentJobTitle.governmentJobTitle.currency === currentContract.annualSalary!.currency

      setValues({
        id: values.id,
        validFrom: values.validFrom,
        changes: { ...formValues, governmentJobTitle: governmentJobTitleStringValue, employeeLevel: employeeLevel },
      })
      setSelectedGovernmentJobTitle(governmentJobTitleValue)
      if (
        !isMonthlySalaryInRange(
          salaryValue / currentContract.monthlySalaryMonthsInYear,
          areCurrenciesTheSame
            ? !!governmentJobTitleValue
              ? governmentJobTitleValue.monthlySalaryMinimum
              : currentContractContractGovernmentJobTitle.governmentJobTitle.monthlySalaryMinimum
            : calculateAmountInNewCurrency(
                !!governmentJobTitleValue
                  ? governmentJobTitleValue.currency
                  : currentContractContractGovernmentJobTitle.governmentJobTitle.currency,
                currentContract.annualSalary!.currency,
                currencyConversionData,
                !!governmentJobTitleValue
                  ? governmentJobTitleValue.monthlySalaryMinimum
                  : currentContractContractGovernmentJobTitle.governmentJobTitle.monthlySalaryMinimum,
              )!,
          areCurrenciesTheSame
            ? !!governmentJobTitleValue
              ? governmentJobTitleValue.monthlySalaryMaximum
              : currentContractContractGovernmentJobTitle.governmentJobTitle.monthlySalaryMaximum
            : calculateAmountInNewCurrency(
                !!governmentJobTitleValue
                  ? governmentJobTitleValue.currency
                  : currentContractContractGovernmentJobTitle.governmentJobTitle.currency,
                currentContract.annualSalary!.currency,
                currencyConversionData,
                !!governmentJobTitleValue
                  ? governmentJobTitleValue.monthlySalaryMaximum
                  : currentContractContractGovernmentJobTitle.governmentJobTitle.monthlySalaryMaximum,
              )!,
        )
      ) {
        if (formValues.salary !== undefined && formValues.salary !== null && formValues.salary.toString() !== '') {
          setMonthlySelectedSalaryWarning(
            t('appraisalMeeting.stepContractChanges.modal.selectedMonthlySalaryOutOfRange', {
              hiringOfficeCode: currentContractContractGovernmentJobTitle.governmentJobTitle.hiringOffice.code,
              department: currentContractContractGovernmentJobTitle.governmentJobTitle.department,
              title: currentContractContractGovernmentJobTitle.governmentJobTitle.title,
              separator: MULTI_VALUE_SEPARATOR,
            }),
          )
          setMonthlyCurrentSalaryWarning(undefined)
        } else {
          setMonthlyCurrentSalaryWarning(
            t('appraisalMeeting.stepContractChanges.modal.currentMonthlySalaryOutOfRange', {
              hiringOfficeCode: currentContractContractGovernmentJobTitle.governmentJobTitle.hiringOffice.code,
              department: currentContractContractGovernmentJobTitle.governmentJobTitle.department,
              title: currentContractContractGovernmentJobTitle.governmentJobTitle.title,
              separator: MULTI_VALUE_SEPARATOR,
            }),
          )
          setMonthlySelectedSalaryWarning(undefined)
        }
      } else {
        setMonthlySelectedSalaryWarning(undefined)
        setMonthlyCurrentSalaryWarning(undefined)
      }
    }
    if (toggleInvalidGovernmentJobTitle) {
      toggleInvalidGovernmentJobTitle(false)
    }
  }

  return (
    <>
      {hasNationalContract &&
        nationalContractLevel &&
        currentContract.nationalContractLevel &&
        currentContract.nationalContractReferenceLevel &&
        isNotApprenticeshipContract &&
        isRowTypeVisible(ContractChangeType.NationalReferenceLevel) &&
        isNotRestricted && (
          <ContractChangeRow
            name={name}
            warnings={futureChangeWarningsByCategory.get(ContractChangeType.NationalReferenceLevel)}
            label="nationalReferenceLevel"
            current={`${t(`employee.nationalContract.shortType.${currentContract.nationalContractLevel.type}`)} ${t(
              `employee.nationalContract.level.${currentContract.nationalContractLevel.level}`,
            )}`}
            change={
              <EmployeeNationalLevelSelect
                currentNationalLevel={nationalContractLevel}
                nationalReferenceLevels={currentContract.nationalContractReferenceLevel.levels}
                type={currentContract.nationalContractLevel.type}
                name={name}
                disabled={disabled}
              />
            }
            mt={undefined}
          />
        )}
      {isRowTypeVisible(ContractChangeType.GovernmentJobTitle) &&
        doSelectableGovernmentJobTitlesExist &&
        governmentJobTitle &&
        isNotRestricted &&
        currentContract.contractGovernmentJobTitle &&
        countrySettings.hasGovernmentJobTitleReferenceLevel && (
          <>
            <ContractChangeRow
              name={name}
              warnings={futureChangeWarningsByCategory.get(ContractChangeType.GovernmentJobTitle)}
              label="hiringOffice"
              current={currentContract.contractGovernmentJobTitle.governmentJobTitle.hiringOffice.code}
              subCurrentFontSize="xs"
              mt={undefined}
            />
            <ContractChangeRow
              name={name}
              warnings={futureChangeWarningsByCategory.get(ContractChangeType.GovernmentJobTitle)}
              label="governmentJobTitle"
              current={`${currentContract.contractGovernmentJobTitle.governmentJobTitle.department}${MULTI_VALUE_SEPARATOR}${currentContract.contractGovernmentJobTitle.governmentJobTitle.title}`}
              subCurrent={
                !!currentContract.contractGovernmentJobTitle && !!currentContract.annualSalary
                  ? [
                      t('profile.contract.salary.monthlySalaryMinimum', {
                        salary: formatCurrency(round(monthlySalaryMinimumInSalaryCurrency!, 2), currentContract.annualSalary.currency),
                      }),
                      t('profile.contract.salary.monthlySalaryMaximum', {
                        salary: formatCurrency(round(monthlySalaryMaximumInSalaryCurrency!, 2), currentContract.annualSalary.currency),
                      }),
                    ]
                  : undefined
              }
              subCurrentFontSize="xs"
              change={
                <GovernmentJobTitleSelect
                  name={name}
                  currentGovernmentJobTitle={governmentJobTitle}
                  selectableGovernmentJobTitles={governmentJobTitles!}
                  onChange={onGovernmentJobTitleChange}
                  disabled={disabled}
                />
              }
              subChange={
                !!currentContract.contractGovernmentJobTitle &&
                countrySettings.hasGovernmentJobTitleReferenceLevel &&
                !!formValues.governmentJobTitle &&
                !!currentContract.annualSalary
                  ? [
                      t('profile.contract.salary.monthlySalaryMinimum', {
                        salary: formatCurrency(
                          selectedGovernmentJobTitle
                            ? round(
                                selectedGovernmentJobTitle.currency !== currentContract.annualSalary.currency
                                  ? calculateAmountInNewCurrency(
                                      selectedGovernmentJobTitle.currency,
                                      currentContract.annualSalary.currency,
                                      currencyConversionData,
                                      selectedGovernmentJobTitle.monthlySalaryMinimum,
                                    )!
                                  : selectedGovernmentJobTitle.monthlySalaryMinimum,
                                2,
                              )
                            : 0,
                          currentContract.annualSalary.currency,
                        ),
                      }),
                      t('profile.contract.salary.monthlySalaryMaximum', {
                        salary: formatCurrency(
                          selectedGovernmentJobTitle
                            ? round(
                                selectedGovernmentJobTitle.currency !== currentContract.annualSalary.currency
                                  ? calculateAmountInNewCurrency(
                                      selectedGovernmentJobTitle.currency,
                                      currentContract.annualSalary!.currency,
                                      currencyConversionData,
                                      selectedGovernmentJobTitle.monthlySalaryMaximum,
                                    )!
                                  : selectedGovernmentJobTitle.monthlySalaryMaximum,
                                2,
                              )
                            : 0,
                          currentContract.annualSalary.currency,
                        ),
                      }),
                    ]
                  : undefined
              }
              subChangeFontSize="xs"
              mt={undefined}
            />
          </>
        )}
      {isRowTypeVisible(ContractChangeType.EmployeeLevel) && isNotRestricted && currentContract.employeeLevel && (
        <ContractChangeRow
          name={name}
          warnings={futureChangeWarningsByCategory.get(ContractChangeType.EmployeeLevel)}
          label={ContractChangeType.EmployeeLevel}
          current={
            !!currentContract.contractGovernmentJobTitle && countrySettings.hasGovernmentJobTitleReferenceLevel
              ? t(`employee.contract.level.${currentContract.contractGovernmentJobTitle.governmentJobTitle.level}`)
              : t(`employee.contract.level.${currentContract.employeeLevel.level}`)
          }
          note={
            values.changes.employeeLevel === EmployeeLevelEnum.Sm ? t('appraisalMeeting.contract.changes.seniorManagerNote') : undefined
          }
          change={
            <EmployeeLevelSelect
              currentEmployeeLevel={employeeLevel}
              name={name}
              isEmployeeLevelValidationEnabled={isEmployeeLevelValidationEnabled}
              disabled={disabled || !!currentContract.contractGovernmentJobTitle || countrySettings.hasGovernmentJobTitleReferenceLevel}
              employeeLevels={employeeLevels}
            />
          }
        />
      )}
      {isRowTypeVisible(ContractChangeType.Salary) && isNotRestricted && currentContract.annualSalary && (
        <ContractChangeRow
          name={name}
          warnings={futureChangeWarningsByCategory.get(ContractChangeType.Salary)}
          label={ContractChangeType.Salary}
          current={formatCurrency(currentContract.annualSalary.amount, currentContract.annualSalary.currency)}
          subCurrent={
            currentContract.isMonthlySalaryDisplayed
              ? !countrySettings.hasGovernmentJobTitleReferenceLevel ||
                (formValues.salary !== undefined && formValues.salary !== null && formValues.salary.toString() !== '')
                ? [
                    t('profile.contract.salary.monthlySalary', {
                      salary: formatCurrency(
                        round(salary / currentContract.monthlySalaryMonthsInYear, 2),
                        currentContract.annualSalary.currency,
                      ),
                    }),
                  ]
                : [
                    t('profile.contract.salary.monthlySalary', {
                      salary: formatCurrency(
                        round(salary / currentContract.monthlySalaryMonthsInYear, 2),
                        currentContract.annualSalary.currency,
                      ),
                    }),
                    monthlyCurrentSalaryWarning && (
                      <PerText mt="xxxs" fontSize="xs" fontWeight="thin" color="warning">
                        {monthlyCurrentSalaryWarning}
                      </PerText>
                    ),
                  ]
              : undefined
          }
          change={
            !!currentContract.contractGovernmentJobTitle && countrySettings.hasGovernmentJobTitleReferenceLevel ? (
              <BrazilianFormikInputNumber
                onBlur={undefined}
                type="currency"
                timezoneAndLocale={timezoneAndLocale}
                currency={currentContract.annualSalary.currency}
                minInitialOffset={salary}
                step={100}
                size="small"
                name={name + '.salary'}
                placeholder={formatCurrency(salary, currentContract.annualSalary.currency, false)}
                subLabel={
                  currentContract.isMonthlySalaryDisplayed && values.changes.salary
                    ? t('profile.contract.salary.monthlySalary', {
                        salary: formatCurrency(
                          round(values.changes.salary / currentContract.monthlySalaryMonthsInYear, 2),
                          currentContract.annualSalary.currency,
                        ),
                      })
                    : undefined
                }
                isContractChange={true}
                monthlySalaryMinimum={
                  selectedGovernmentJobTitle
                    ? selectedGovernmentJobTitle.monthlySalaryMinimum
                    : currentContract.contractGovernmentJobTitle
                    ? currentContract.contractGovernmentJobTitle.governmentJobTitle.monthlySalaryMinimum
                    : undefined
                }
                monthlySalaryMaximum={
                  selectedGovernmentJobTitle
                    ? selectedGovernmentJobTitle.monthlySalaryMaximum
                    : currentContract.contractGovernmentJobTitle
                    ? currentContract.contractGovernmentJobTitle.governmentJobTitle.monthlySalaryMaximum
                    : undefined
                }
                t={t}
                currentGovernmentJobTitle={currentGovernmentJobTitle}
                currentContractSalary={currentContract.annualSalary ? currentContract.annualSalary.amount : undefined}
                monthlySalaryMonthsInYear={currentContract.monthlySalaryMonthsInYear}
                selectedSalaryWarning={monthlySelectedSalaryWarning}
                setSelectedSalaryWarning={setMonthlySelectedSalaryWarning}
                setCurrentSalaryWarning={setMonthlyCurrentSalaryWarning}
                data-e2e-contract-change-salary-input
                style={{ border: monthlySelectedSalaryWarning ? '1px solid #fcc227' : undefined, width: '120px' }}
                disabled={disabled}
              />
            ) : (
              <InputNumber
                onBlur={undefined}
                type="currency"
                timezoneAndLocale={timezoneAndLocale}
                currency={currentContract.annualSalary.currency}
                minInitialOffset={salary}
                step={100}
                size="small"
                name={name + '.salary'}
                placeholder={formatCurrency(salary, currentContract.annualSalary.currency, false)}
                subLabel={
                  currentContract.isMonthlySalaryDisplayed && values.changes.salary
                    ? t('profile.contract.salary.monthlySalary', {
                        salary: formatCurrency(
                          round(values.changes.salary / currentContract.monthlySalaryMonthsInYear, 2),
                          currentContract.annualSalary.currency,
                        ),
                      })
                    : undefined
                }
                isContractChange={true}
                disabled={disabled}
                data-e2e-contract-change-salary-input
              />
            )
          }
        />
      )}
      {isRowTypeVisible(ContractChangeType.Bonus) && !isTargetBonusChangeNotAllowed && (
        <ContractChangeRow
          name={name}
          warnings={futureChangeWarningsByCategory.get(ContractChangeType.Bonus)}
          label={ContractChangeType.Bonus}
          current={
            currentContract.targetBonus ? formatCurrency(currentContract.targetBonus.amount, currentContract.targetBonus.currency) : '-'
          }
          change={
            <InputNumber
              type="currency"
              currency={currentContract.targetBonus ? currentContract.targetBonus.currency : fallbackCurrency}
              timezoneAndLocale={timezoneAndLocale}
              minInitialOffset={bonus ? bonus : currentContract.targetBonus ? currentContract.targetBonus.amount : 0}
              step={100}
              name={`${name}.${ContractChangeType.Bonus}`}
              size="small"
              placeholder={formatCurrency(
                bonus ? bonus : currentContract.targetBonus ? currentContract.targetBonus.amount : 0,
                currentContract.targetBonus ? currentContract.targetBonus.currency : fallbackCurrency,
                false,
              )}
              isContractChange={true}
              data-e2e-contract-change-bonus-input
              disabled={disabled}
            />
          }
        />
      )}
      {isNotRestricted && !!careerPathEvaluation && (
        <ContractChangeCareerPath
          careerPathEvaluation={careerPathEvaluation}
          formValues={formValues}
          currentContract={currentContract}
          isPreparation={isPreparation}
        />
      )}
    </>
  )
}
